import { JsonObject } from '@mainstage/types';
import {
  ConfirmDialog,
  WbDockablePanel,
  WbEditor,
  WbInfoTooltip,
  WbTooltip,
} from '@agilelab/plugin-wb-platform';
import { IconButton, makeStyles } from '@material-ui/core';
import DeleteSweepIcon from '@material-ui/icons/DeleteSweep';
import { useState } from 'react';

const useStyles = makeStyles(
  theme => ({
    button: {
      padding: theme.spacing(1),
    },
    infoIcon: {
      fontSize: 20,
      opacity: 0.6,
    },
  }),
  { name: 'LiveFormParametersPanel' },
);

interface LiveFormParametersPanelProps {
  formContext: JsonObject;
  onClear: () => void;
  defaultSize: number;
  minSize: number;
  primary?: boolean;
  className?: string;
  cardHeaderStyles?: React.CSSProperties;
}

export function LiveFormParametersPanel({
  formContext,
  onClear,
  defaultSize,
  minSize,
  primary = false,
  className,
  cardHeaderStyles,
}: LiveFormParametersPanelProps) {
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <WbDockablePanel
      defaultSize={defaultSize}
      minSize={minSize}
      primary={primary}
      title="Live Form Parameters"
      className={className}
      cardHeaderStyles={cardHeaderStyles}
      headerActions={
        <>
          <WbInfoTooltip
            title="Live snapshot of the form's current field values, updated in real time as you fill in the form. Use this to understand the target parameters structure of your skeleton `parameters.yaml`, as well as to configure correctly the different pickers in your form."
            variant="standard"
          />
          <WbTooltip title="Clear form context">
            <IconButton
              className={classes.button}
              onClick={() => setDialogOpen(true)}
              aria-label="Clear form context"
            >
              <DeleteSweepIcon />
            </IconButton>
          </WbTooltip>
        </>
      }
    >
      <WbEditor
        language="json"
        options={{
          readOnly: true,
          minimap: { enabled: false },
          ariaLabel: 'Form Parameters Inspector',
        }}
        value={JSON.stringify(formContext, null, 2)}
      />
      <ConfirmDialog
        open={dialogOpen}
        title="Clear form context?"
        description="This will reset all form fields to their initial state and return to the first step. This action cannot be undone."
        confirmButtonText="Confirm"
        onClose={() => setDialogOpen(false)}
        onConfirm={() => {
          setDialogOpen(false);
          onClear();
        }}
      />
    </WbDockablePanel>
  );
}
